/**
 * ClientRatesTab — Admin > Client rates (the founder's "can I please
 * have the admin screen to set those per client" ask; B1-D3c).
 *
 * One table: a row per ACTIVE client (A→Z), a column per CONFIGURED
 * rate band (the same #456 vocabulary the PUT validates against — the
 * columns arrive with the aggregate response, so the grid can never
 * drift from the write gate). Each cell shows the rate in effect today
 * with its quiet "from" date, or "No rate on file" as a QUIET fact: a
 * missing rate refuses billing on THAT band for that client and
 * nothing else, and most clients never use every band, so an empty
 * cell is not an alarm. The Pre-billing row of a note on a band with
 * no rate is where the block is named.
 *
 * Below the grid, rate managers keep the tenant's STANDARD RATE SETS
 * (RateSetsEditor; tenants.config billing.rate_sets): the named lists
 * a new client can be pre-filled from.
 *
 * Band-applicability note: the role-scoped band matrix (0134) scopes
 * which bands STAFF may pick when logging time; it does NOT scope
 * clients. Every configured band is a column — the owner decides which
 * bands a client uses by setting them.
 *
 * Clicking a cell opens the SAME SetRateDialog the Pre-billing Rates
 * card uses (one write surface); a save invalidates the aggregate
 * query so the grid refetches. manage_client_rates-gated (the PUT's
 * gate — owner + the bookkeeping seats); the nav hides this view
 * without the capability, and cells render read-only defensively.
 *
 * BULK (founder feedback 2026-07-10): rate managers get a checkbox per
 * client row + a select-all, and "Set rate for selected" opens
 * BulkSetRateDialog — one band + rate + from, applied through the same
 * per-client PUT, sequentially, with named per-client failures.
 */

import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { ArchiveRateDialog } from '@/components/ArchiveRateDialog';
import { BulkSetRateDialog } from '@/components/BulkSetRateDialog';
import { RateSetsEditor } from '@/components/RateSetsEditor';
import { SetRateDialog } from '@/components/SetRateDialog';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { useTenant } from '@/lib/auth';
import { canManageClientRates } from '@/lib/capabilities';
import type { RateBandOption } from '@/lib/notes';
import { proseDate } from '@/lib/proseDate';
import { practiceTodayIso } from '@/lib/practiceDate';
import {
  clientRatesOverviewKey,
  fetchClientRatesOverview,
  formatMoney,
  rateUnit,
  type ClientRatesOverviewClientWire,
  type ClientRatesOverviewResponse,
  type ResolvedRateWire,
} from '@/lib/clientRates';

export interface ClientRatesTabProps {
  tenantSlug: string;
}

/** One client+band cell: the current rate (with its quiet "from") or
 *  the no-rate attention register. A button for rate managers, plus a
 *  row-level Archive affordance on the rate in effect (0138 — the way
 *  a mistaken "ghost" rate leaves the matrix; archived rows never
 *  reach the current pick, so a successful archive clears the cell to
 *  the next active rate or "No rate on file"). */
function RateCell({
  clientId,
  band,
  value,
  upcoming,
  canManage,
  onEdit,
  onArchive,
}: {
  clientId: string;
  band: { value: string; label: string; per_visit?: true };
  value: ResolvedRateWire | null;
  /** The next rate to start, shown as starting later (never as no rate). */
  upcoming: ResolvedRateWire | null;
  canManage: boolean;
  onEdit: () => void;
  onArchive: () => void;
}): ReactNode {
  const unit = rateUnit(band.per_visit === true);
  const next =
    upcoming === null ? null : (
      <span
        className={value ? 'block text-xs text-ink-soft' : 'text-sm text-ink'}
        data-testid={`client-rates-upcoming-${clientId}-${band.value}`}
      >
        {value ? 'then ' : ''}
        {formatMoney(upcoming.rate)}, from {proseDate(upcoming.effective_from)}
      </span>
    );
  const content = value ? (
    <>
      <span className="text-sm text-ink" data-testid={`client-rates-current-${clientId}-${band.value}`}>
        {formatMoney(value.rate)}
      </span>
      <span className="block text-xs text-ink-soft">from {proseDate(value.effective_from)}</span>
      {next}
    </>
  ) : upcoming !== null ? (
    next
  ) : (
    <span
      className="text-sm italic text-ink-soft"
      data-register="quiet"
      data-testid={`client-rates-norate-${clientId}-${band.value}`}
    >
      No rate on file
    </span>
  );

  if (!canManage) {
    return <div className="px-2 py-1.5">{content}</div>;
  }
  return (
    <div>
      <button
        type="button"
        data-testid={`client-rates-cell-${clientId}-${band.value}`}
        aria-label={`${band.label}: ${
          value
            ? `${formatMoney(value.rate)} ${unit}`
            : upcoming !== null
              ? `${formatMoney(upcoming.rate)} ${unit} from ${proseDate(upcoming.effective_from)}`
              : 'no rate on file'
        }. ${value || upcoming !== null ? 'Change' : 'Set'} rate.`}
        onClick={onEdit}
        className="w-full rounded-base px-2 py-1.5 text-left transition-colors duration-[var(--dur-base)] ease-quiet hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        {content}
      </button>
      {value && (
        <button
          type="button"
          data-testid={`client-rates-archive-${clientId}-${band.value}`}
          aria-label={`${band.label}: archive the ${formatMoney(value.rate)} rate.`}
          onClick={onArchive}
          className="px-2 pb-1 text-xs text-ink-soft underline-offset-2 hover:text-sage-800 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Archive
        </button>
      )}
    </div>
  );
}

export function ClientRatesTab({ tenantSlug }: ClientRatesTabProps): ReactNode {
  const { whoami } = useTenant();
  const canManage = canManageClientRates(whoami);
  const queryClient = useQueryClient();
  // The practice's day (the RatesCard's today on the record and on Billing):
  // the grid's "current" pick and the dialog's date default agree.
  const today = practiceTodayIso();

  const query = useQuery<ClientRatesOverviewResponse, Error>({
    queryKey: clientRatesOverviewKey(tenantSlug, today),
    queryFn: ({ signal }) => fetchClientRatesOverview(tenantSlug, today, signal),
    staleTime: 30_000,
  });

  const [editing, setEditing] = useState<{
    client: ClientRatesOverviewClientWire;
    band: RateBandOption;
    initialRate: string;
    initialFrom?: string;
  } | null>(null);
  const [archiving, setArchiving] = useState<{
    client: ClientRatesOverviewClientWire;
    band: RateBandOption;
    rate: ResolvedRateWire;
  } | null>(null);

  // Bulk (founder feedback 2026-07-10): the selected client ids + the
  // one-band-for-many dialog. Selection only exists for rate managers.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  function toggleSelected(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const clients = query.data?.clients ?? [];
  const allSelected = clients.length > 0 && clients.every((c) => selected.has(c.id));
  const selectedClients = clients.filter((c) => selected.has(c.id));

  return (
    <div data-testid="client-rates-tab">
      <h2 className="font-display text-lg text-sage-800">Client rates</h2>
      <p className="mt-1 text-sm text-ink-soft">
        Each client&rsquo;s hourly rate for each kind of work. Click a rate to change it,
        or set one where it&rsquo;s missing. A kind of work with no rate can&rsquo;t be billed for
        that client; the others bill as usual.
      </p>

      {query.isError && (
        <div className="mt-4">
          <ErrorBanner error={query.error} onRetry={() => void query.refetch()} what="the client rates" />
        </div>
      )}
      {query.isPending && (
        <div className="mt-6 space-y-2">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.data && query.data.clients.length === 0 && (
        <p className="mt-6 text-sm text-ink-soft" data-testid="client-rates-empty">
          Add a client and you'll see them here.
        </p>
      )}

      {/* Bulk action bar — rate managers only (the PUT's gate). */}
      {query.data && query.data.clients.length > 0 && canManage && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={selectedClients.length === 0}
            onClick={() => setBulkOpen(true)}
            data-testid="client-rates-bulk-open"
          >
            Set rate for selected
            {selectedClients.length > 0 ? ` (${selectedClients.length})` : ''}
          </Button>
          {selectedClients.length === 0 && (
            <p className="text-xs text-ink-soft">
              Tick clients below to set one rate for all of them at once.
            </p>
          )}
        </div>
      )}

      {query.data && query.data.clients.length > 0 && (
        <div className={(canManage ? 'mt-3' : 'mt-6') + ' overflow-x-auto'}>
          <table className="w-full text-sm" data-testid="client-rates-table">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-400">
                {canManage && (
                  <th className="py-1.5 pr-2 font-semibold">
                    <input
                      type="checkbox"
                      aria-label="Select all clients"
                      data-testid="client-rates-select-all"
                      checked={allSelected}
                      onChange={() =>
                        setSelected(allSelected ? new Set() : new Set(clients.map((c) => c.id)))
                      }
                    />
                  </th>
                )}
                <th className="py-1.5 pr-3 font-semibold">Client</th>
                {query.data.bands.map((b) => (
                  <th key={b.value} className="py-1.5 px-2 font-semibold whitespace-nowrap">
                    {b.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-sage-800/[6%]">
              {query.data.clients.map((client) => (
                <tr key={client.id} data-testid={`client-rates-row-${client.id}`}>
                  {canManage && (
                    <td className="py-2 pr-2 align-top">
                      <input
                        type="checkbox"
                        aria-label={`Select ${client.display_name}`}
                        data-testid={`client-rates-select-${client.id}`}
                        checked={selected.has(client.id)}
                        onChange={() => toggleSelected(client.id)}
                      />
                    </td>
                  )}
                  <td className="py-2 pr-3 align-top whitespace-nowrap">
                    <Link
                      to={`/t/${tenantSlug}/clients/${client.id}?tab=pre-billing`}
                      className="text-sm font-medium text-sage-800 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                    >
                      {client.display_name}
                    </Link>
                  </td>
                  {query.data.bands.map((band) => (
                    <td key={band.value} className="py-1 px-1 align-top">
                      <RateCell
                        clientId={client.id}
                        band={band}
                        value={client.rates[band.value] ?? null}
                        upcoming={client.upcoming?.[band.value] ?? null}
                        canManage={canManage}
                        onEdit={() => {
                          // A rate that has not started opens on its own
                          // amount and date, so a correction replaces it.
                          const current = client.rates[band.value] ?? null;
                          const next = client.upcoming?.[band.value] ?? null;
                          setEditing({
                            client,
                            band,
                            initialRate: (current ?? next)?.rate ?? '',
                            initialFrom: current === null && next !== null ? next.effective_from : undefined,
                          });
                        }}
                        onArchive={() => {
                          const rate = client.rates[band.value];
                          if (rate) setArchiving({ client, band, rate });
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {query.data && canManage && <RateSetsEditor tenantSlug={tenantSlug} bands={query.data.bands} />}

      {bulkOpen && query.data && (
        <BulkSetRateDialog
          bands={query.data.bands}
          clients={selectedClients.map((c) => ({ id: c.id, display_name: c.display_name }))}
          today={today}
          onApplied={() => {
            // Refetch the aggregate — every rate that landed shows,
            // whether or not the whole run succeeded.
            void queryClient.invalidateQueries({
              queryKey: clientRatesOverviewKey(tenantSlug, today),
            });
          }}
          onClose={() => setBulkOpen(false)}
        />
      )}

      {editing && (
        <SetRateDialog
          band={editing.band}
          clientId={editing.client.id}
          initialRate={editing.initialRate}
          initialFrom={editing.initialFrom}
          today={today}
          title={`${editing.band.label} rate for ${editing.client.display_name}`}
          onSaved={() => {
            // Refetch the aggregate — the grid re-renders from the fresh pick.
            void queryClient.invalidateQueries({
              queryKey: clientRatesOverviewKey(tenantSlug, today),
            });
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {archiving && (
        <ArchiveRateDialog
          clientId={archiving.client.id}
          rate={archiving.rate}
          bandLabel={archiving.band.label}
          title={`Archive ${archiving.band.label} rate for ${archiving.client.display_name}`}
          archived={true}
          onDone={() => {
            // Refetch the aggregate — the archived row leaves the pick;
            // the cell shows the next active rate or "No rate on file".
            void queryClient.invalidateQueries({
              queryKey: clientRatesOverviewKey(tenantSlug, today),
            });
          }}
          onClose={() => setArchiving(null)}
        />
      )}
    </div>
  );
}
